
import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Spinner, InputGroup, Modal, Table } from 'react-bootstrap';
import { FiEdit2, FiPlus, FiSearch, FiTrash2, FiUser } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import api from '../../service/api';
import './Questions.css';

const Questions = () => {
  const { token: authToken, loading } = useAuth();
  const { showSuccess, showError } = useToast();

  const [questions, setQuestions] = useState([]);
  const [telecallers, setTelecallers] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [targetTelecallerId, setTargetTelecallerId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [newQuestionText, setNewQuestionText] = useState('');
  const [newQuestionType, setNewQuestionType] = useState('radio');
  const [newQuestionOptions, setNewQuestionOptions] = useState('');
  const [newIsRequired, setNewIsRequired] = useState(false);

  const [assigning, setAssigning] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({
    questionText: '',
    questionType: 'radio',
    options: [],
    isRequired: false
  });
  const [editOptionsInput, setEditOptionsInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!loading && authToken) {
      fetchQuestions();
      fetchTelecallers();
    }
  }, [authToken, loading]);

  const fetchQuestions = async () => {
    try {
      const res = await api.get('/api/questions');
      setQuestions(res.data);
    } catch {
      showError('Failed to fetch questions.');
    }
  };

  const fetchTelecallers = async () => {
    try {
      const res = await api.get('/api/admin/telecallers');
      setTelecallers(res.data);
    } catch {}
  };

  const handleAddQuestion = async (e) => {
    e.preventDefault();

    if (!newQuestionText.trim()) return showError('Question text cannot be empty.');

    let options = [];
    if (newQuestionType !== 'text') {
      options = newQuestionOptions.split(',').map(o => o.trim()).filter(Boolean);
      if (!options.length) return showError('Please provide at least one option.');
    }

    try {
      await api.post('/api/questions', {
        questionText: newQuestionText,
        questionType: newQuestionType,
        options,
        isRequired: newIsRequired
      });

      showSuccess('Question added successfully!');
      setNewQuestionText('');
      setNewQuestionOptions('');
      setNewIsRequired(false);
      setShowCreateModal(false);
      fetchQuestions();
    } catch {
      showError('Failed to add question.');
    }
  };

  const handleSelectQuestion = (id) => {
    setSelectedQuestions(prev => prev.includes(id) ? prev.filter(q => q !== id) : [...prev, id]);
  };

  const handleAssignQuestions = async () => {
    if (!targetTelecallerId) return showError('Please select a telecaller.');
    if (!selectedQuestions.length) return showError('Select questions first.');

    setAssigning(true);
    try {
      const res = await api.post('/api/questions/assign', {
        questionIds: selectedQuestions,
        userId: targetTelecallerId
      });
      showSuccess(res.data.message);
      setSelectedQuestions([]);
      fetchQuestions();
    } catch {
      showError('Assignment failed.');
    } finally {
      setAssigning(false);
    }
  };

  const handleDeleteQuestion = async (id) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await api.delete(`/api/questions/${id}`);
      fetchQuestions();
    } catch {
      showError('Delete failed.');
    }
  };

  const handleEditQuestion = (question) => {
    setEditingId(question._id);
    setEditData({
      questionText: question.questionText,
      questionType: question.questionType,
      options: question.options || [],
      isRequired: Boolean(question.isRequired)
    });
    setEditOptionsInput((question.options || []).join(', '));
  };

  const handleSaveQuestion = async () => {
    const questionText = editData.questionText.trim();
    if (!questionText) return showError('Question text cannot be empty.');

    const options = editData.questionType === 'text'
      ? []
      : editOptionsInput.split(',').map(option => option.trim()).filter(Boolean);

    if (editData.questionType !== 'text' && !options.length) {
      return showError('Please provide at least one option.');
    }

    setIsSaving(true);
    try {
      await api.put(`/api/questions/${editingId}`, {
        ...editData,
        questionText,
        options
      });
      showSuccess('Question updated successfully!');
      setEditingId(null);
      fetchQuestions();
    } catch {
      showError('Failed to update question.');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredQuestions = questions.filter(question => {
    const searchText = searchQuery.trim().toLowerCase();
    return !searchText || [
      question.questionText,
      ...(question.options || [])
    ].some(value => value?.toLowerCase().includes(searchText));
  });

  const allFilteredSelected = filteredQuestions.length > 0
    && filteredQuestions.every(question => selectedQuestions.includes(question._id));

  const handleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredQuestions.map(question => question._id));
      setSelectedQuestions(prev => prev.filter(id => !filteredIds.has(id)));
      return;
    }

    setSelectedQuestions(prev => [
      ...new Set([...prev, ...filteredQuestions.map(question => question._id)])
    ]);
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="grow" />
        <p className="fw-bold text-muted mt-3">Synchronizing Question Bank...</p>
      </Container>
    );
  }

  const questionFormFields = (prefix, text, setText, type, setType, optionsInput, setOptionsInput, isRequired, setRequired) => (
    <>
      <Form.Group className="question-bank-field">
        <Form.Label htmlFor={`${prefix}-question-text`}>Question text</Form.Label>
        <Form.Control
          id={`${prefix}-question-text`}
          placeholder="e.g. Will you vote in the next election?"
          value={text}
          onChange={e => setText(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="question-bank-field">
        <Form.Label htmlFor={`${prefix}-question-type`}>Question type</Form.Label>
        <Form.Select id={`${prefix}-question-type`} value={type} onChange={e => setType(e.target.value)}>
          <option value="radio">Single Choice (Radio)</option>
          <option value="checkbox">Multiple Choice (Checkbox)</option>
          <option value="text">Free Text Input</option>
        </Form.Select>
      </Form.Group>

      {type !== 'text' && (
        <Form.Group className="question-bank-field">
          <Form.Label htmlFor={`${prefix}-question-options`}>Options</Form.Label>
          <Form.Control
            id={`${prefix}-question-options`}
            placeholder="Yes, No, Undecided"
            value={optionsInput}
            onChange={e => setOptionsInput(e.target.value)}
          />
          <Form.Text className="text-muted">Separate options with commas.</Form.Text>
        </Form.Group>
      )}

      <Form.Check
        type="switch"
        id={`${prefix}-question-required`}
        label="Mandatory Field"
        className="question-bank-required"
        checked={isRequired}
        onChange={e => setRequired(e.target.checked)}
      />
    </>
  );

  return (
    <Container fluid className="question-bank px-3 px-lg-4">
      <div className="question-bank-header">
        <div className="question-bank-title">
          <h1 className="fw-bold mb-1">Question Bank</h1>
          <p className="text-secondary mb-0">Manage and assign data collection points</p>
        </div>

        <div className="question-bank-stats">
          <Card className="question-bank-stat">
            <small>Total Questions</small>
            <div className="question-bank-stat-value">{questions.length}</div>
          </Card>

          <Card className={`question-bank-stat ${selectedQuestions.length ? 'is-selected' : ''}`}>
            <small>Selected</small>
            <div className="question-bank-stat-value">{selectedQuestions.length}</div>
          </Card>
        </div>
      </div>

      <Card className="question-bank-panel">
        <div className="question-bank-toolbar">
          <Button
            className="question-bank-create-button fw-bold"
            onClick={() => setShowCreateModal(true)}
          >
            <FiPlus aria-hidden="true" /> Create New Question
          </Button>

          <InputGroup className="question-bank-search">
            <InputGroup.Text><FiSearch aria-hidden="true" /></InputGroup.Text>
            <Form.Control
              type="search"
              aria-label="Search questions"
              placeholder="Search questions..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </InputGroup>

          <InputGroup className="question-bank-operator-group">
            <InputGroup.Text><FiUser aria-hidden="true" /></InputGroup.Text>
            <Form.Select
              className="question-bank-operator"
              aria-label="Choose operator"
              value={targetTelecallerId}
              onChange={e => setTargetTelecallerId(e.target.value)}
            >
              <option value="">Choose operator...</option>
              {telecallers.map(t => (
                <option key={t._id} value={t._id}>{t.username}</option>
              ))}
            </Form.Select>
          </InputGroup>

          <Button
            className="question-bank-assign-button fw-bold"
            onClick={handleAssignQuestions}
            disabled={!selectedQuestions.length || assigning}
          >
            {assigning ? <Spinner size="sm" /> : 'Assign Selected'}
          </Button>
        </div>

        <div className="question-bank-table-wrap" aria-live="polite">
          <Table className="question-bank-table mb-0" hover>
            <colgroup>
              <col className="col-select" />
              <col className="col-index" />
              <col className="col-type" />
              <col className="col-question" />
              <col className="col-options" />
              <col className="col-status" />
              <col className="col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th className="cell-select">
                  <Form.Check
                    type="checkbox"
                    className="question-bank-select"
                    aria-label="Select all questions"
                    checked={allFilteredSelected}
                    onChange={handleSelectAllFiltered}
                    disabled={!filteredQuestions.length}
                  />
                </th>
                <th className="cell-index">#</th>
                <th className="cell-type">Type</th>
                <th className="cell-question">Question</th>
                <th className="cell-options">Options</th>
                <th className="cell-status">Status</th>
                <th className="cell-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuestions.map((q, index) => (
                <tr key={q._id} className={selectedQuestions.includes(q._id) ? 'is-selected' : ''}>
                  <td className="cell-select">
                    <Form.Check
                      type="checkbox"
                      className="question-bank-select"
                      aria-label={`Select question: ${q.questionText}`}
                      checked={selectedQuestions.includes(q._id)}
                      onChange={() => handleSelectQuestion(q._id)}
                    />
                  </td>
                  <td className="cell-index">{index + 1}</td>
                  <td className="cell-type">
                    <span className={`question-bank-type question-bank-type-${q.questionType}`}>
                      {q.questionType}
                    </span>
                  </td>
                  <td className="cell-question question-bank-question-text">{q.questionText}</td>
                  <td className="cell-options">
                    <div className="question-bank-options">
                      {(q.options || []).map((option, optionIndex) => (
                        <span key={`${q._id}-${optionIndex}`}>{option}</span>
                      ))}
                    </div>
                  </td>
                  <td className="cell-status">
                    <span className={`question-bank-assigned ${q.assignedTo?.length ? 'is-assigned' : 'is-unassigned'}`}>
                      {q.assignedTo?.length ? 'Assigned' : 'Unassigned'}
                    </span>
                  </td>
                  <td className="cell-actions">
                    <div className="question-bank-actions">
                      <Button
                        variant="link"
                        className="question-bank-icon-button question-bank-edit"
                        aria-label="Edit question"
                        onClick={() => handleEditQuestion(q)}
                      >
                        <FiEdit2 aria-hidden="true" />
                      </Button>
                      <Button
                        variant="link"
                        className="question-bank-icon-button question-bank-delete"
                        aria-label="Delete question"
                        onClick={() => handleDeleteQuestion(q._id)}
                      >
                        <FiTrash2 aria-hidden="true" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {filteredQuestions.length === 0 && (
            <div className="question-bank-empty">
              {searchQuery.trim() ? 'No questions match your search.' : 'No questions have been added yet.'}
            </div>
          )}
        </div>
      </Card>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create New Question</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleAddQuestion}>
          <Modal.Body>
            {questionFormFields(
              'new',
              newQuestionText,
              setNewQuestionText,
              newQuestionType,
              setNewQuestionType,
              newQuestionOptions,
              setNewQuestionOptions,
              newIsRequired,
              setNewIsRequired
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Add to Bank</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={editingId !== null} onHide={() => setEditingId(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {questionFormFields(
            'edit',
            editData.questionText,
            value => setEditData({ ...editData, questionText: value }),
            editData.questionType,
            value => setEditData({ ...editData, questionType: value }),
            editOptionsInput,
            setEditOptionsInput,
            editData.isRequired,
            value => setEditData({ ...editData, isRequired: value })
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingId(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveQuestion} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Questions;
